import type { Theme } from "@mui/material/styles";
import type { AppNavbarShellStyles, MdTheme, NavbarOwnerState, NavbarRowOwnerState } from "../types";

function buildAppBar(theme: MdTheme, ownerState: NavbarOwnerState = {}) {
  const { palette, boxShadows, functions, transitions, breakpoints, borders } = theme;
  const { transparentNavbar, absolute, darkMode } = ownerState;

  const { white, dark, text, background, grey } = palette;
  const { navbarBoxShadow } = boxShadows;
  const { rgba, pxToRem } = functions;
  const { borderRadius } = borders;

  return {
    boxShadow: absolute ? "none" : navbarBoxShadow,
    // Acrylic-style layer: mostly opaque so text stays crisp, blur only softens what scrolls beneath.
    backdropFilter: absolute ? "none" : `saturate(125%) blur(${pxToRem(20)})`,
    backgroundColor: rgba(background.paper, 0.86),
    border: `1px solid ${background.cardBorder}`,
    color: text?.main || dark.main,
    top: absolute ? 0 : pxToRem(8),
    minHeight: pxToRem(64),
    display: "grid",
    alignItems: "center",
    borderRadius: borderRadius.lg,
    paddingTop: pxToRem(6),
    paddingBottom: pxToRem(6),
    paddingRight: absolute ? pxToRem(8) : 0,
    paddingLeft: absolute ? pxToRem(12) : 0,
    "& > *": {
      transition: transitions.create("all", {
        easing: transitions.easing.easeInOut,
        duration: transitions.duration.shorter,
      }),
    },
    "& .MuiToolbar-root": {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      minHeight: pxToRem(52),
      [breakpoints.up("sm")]: {
        minHeight: pxToRem(52),
        paddingLeft: pxToRem(16),
        paddingRight: pxToRem(16),
        paddingTop: pxToRem(8),
        paddingBottom: pxToRem(8),
      },
    },
  };
}

export function buildNavbarStyles(theme: Theme): AppNavbarShellStyles {
  const mdTheme = theme as MdTheme;
  const { breakpoints, typography } = mdTheme;
  const { size } = typography;

  return {
    appBar: (ownerState) => buildAppBar(mdTheme, ownerState),
    toolbar: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 1,
      // Balanced inset so breadcrumb / actions don’t sit on the chrome edges.
      pt: { xs: 0.75, sm: 1 },
      pb: { xs: 0.75, sm: 1 },
      px: { xs: 1.25, sm: 2 },
      minHeight: { xs: 56, sm: 64 },
    },
    row: (ownerState: NavbarRowOwnerState = {}) => {
      const { isMini = false } = ownerState;
      return {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        [breakpoints.up("md")]: {
          justifyContent: isMini ? "space-between" : "stretch",
          width: isMini ? "100%" : "max-content",
        },
        [breakpoints.up("xl")]: {
          justifyContent: "stretch !important",
          width: "max-content !important",
        },
      };
    },
    iconButton: {
      px: 1,
      "& .material-icons, .material-icons-round": {
        fontSize: `${size.xl} !important`,
      },
      "& .MuiTypography-root": {
        display: "none",
        [breakpoints.up("sm")]: {
          display: "inline-block",
          lineHeight: 1.2,
          ml: 0.5,
        },
      },
    },
    mobileMenuButton: {
      display: "inline-block",
      lineHeight: 0,
      [breakpoints.up("xl")]: {
        display: "none",
      },
    },
    iconColor: (darkMode = false) => ({
      color: darkMode ? "#ffffff !important" : "text.main !important",
    }),
    userAvatar: {
      width: 32,
      height: 32,
      bgcolor: "primary.main",
      fontSize: "0.875rem",
    },
    userName: (darkMode = false) => ({
      color: darkMode ? "#ffffff" : "text.primary",
      fontWeight: "medium",
      fontSize: "0.875rem",
      lineHeight: 1.2,
    }),
    userEmail: (darkMode = false) => ({
      color: darkMode ? "rgba(255,255,255,0.82)" : "text.secondary",
      fontSize: "0.75rem",
      lineHeight: 1,
    }),
    defaultNavbarShell: (ownerState = {}) => {
      const { transparent = false, light = false } = ownerState;
      const { palette } = mdTheme;
      const { transparent: transparentColor, white, background } = palette;
      return {
        backgroundColor: transparent
          ? transparentColor.main
          : light
            ? white.main
            : background.sidenav,
      };
    },
    defaultNavbarBrand: (light = false) => ({
      color: light ? "white" : "text.primary",
    }),
    defaultNavbarLink: {
      cursor: "pointer",
      userSelect: "none",
    },
    defaultNavbarLinkIcon: (light = false) => ({
      color: light ? "common.white" : "secondary.main",
      verticalAlign: "middle",
    }),
    defaultNavbarLinkText: {
      width: "100%",
      lineHeight: 0,
    },
    defaultNavbarActionButton: {
      mt: -0.3,
    },
    defaultNavbarMobileToggle: {
      cursor: "pointer",
    },
    mobileMenuList: (width: number) => ({
      width: `calc(${width}px - 4rem)`,
    }),
  };
}
